/**
 * Bid Optimizer detail route — /bid-optimizer/:id
 *
 * Owns the single combined fetch for this page: jobConfig + jobRun (status)
 * and, once status is completed, the bid-multiplier output too — all behind
 * one "loading" flag, so the page shows exactly one spinner instead of one
 * for the status check followed by a second one inside BidOptimizerResults.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Spin } from "antd";
import axiosInstance from "../../../utils/axiosInstance";
import { JobType } from "../../../constants/JobTypes";
import BidOptimizerResults from "../components/BidOptimizerResults";
import { parseBidMultiplierOutput, BidOptimizerColumn, BidOptimizerRow } from "../components/parseBidMultiplierOutput";

type RunStatus = "pending" | "running" | "completed" | "failed" | "unknown";

function mapStatus(configStatus: string, runStatus?: string): RunStatus {
  const run = (runStatus || "").toUpperCase();
  const cfg = (configStatus || "").toLowerCase();
  if (run === "RUNNING") return "running";
  if (run === "PENDING") return "pending";
  if (run === "SUCCESSFUL" || run === "SUCCESS") return "completed";
  if (run === "FAILED") return "failed";
  if (cfg.includes("success") || cfg.includes("complete")) return "completed";
  if (cfg === "running") return "running";
  if (cfg.includes("fail")) return "failed";
  if (cfg === "pending") return "pending";
  return "unknown";
}

const STATUS_META: Record<string, { label: string; color: string }> = {
  pending:   { label: "Queued",  color: "#3b82f6" },
  running:   { label: "Running", color: "#f59e0b" },
  failed:    { label: "Failed",  color: "#ef4444" },
  unknown:   { label: "Pending", color: "#3b82f6" },
};

export default function BidOptimizerDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [name, setName] = useState<string>("Bid Optimizer Job");
  const [status, setStatus] = useState<RunStatus>("unknown");
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [jobConfigData, setJobConfigData] = useState<any>(null);
  const [outputColumns, setOutputColumns] = useState<BidOptimizerColumn[]>([]);
  const [outputRows, setOutputRows] = useState<BidOptimizerRow[]>([]);
  const [outputUnavailable, setOutputUnavailable] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const [cfgRes, runRes] = await Promise.allSettled([
        axiosInstance.get(`/api/jobConfig/${id}`),
        axiosInstance.get(`/api/jobRun/jobType/${JobType.BidOptimizer}?limit=200`),
      ]);

      if (cfgRes.status === "rejected") {
        setNotFound(true);
        return;
      }
      const jc = cfgRes.value.data;
      setName(jc?.name || "Bid Optimizer Job");
      setJobConfigData(jc);

      const runs: any[] = runRes.status === "fulfilled"
        ? (Array.isArray(runRes.value.data) ? runRes.value.data : runRes.value.data?.items ?? [])
        : [];
      const matching = runs.filter(r => r.jobConfigId === id);
      const latest = matching.sort((a, b) => new Date(b.createdDate || b.jobExecutionTime || 0).getTime() - new Date(a.createdDate || a.jobExecutionTime || 0).getTime())[0];

      const resolvedStatus = mapStatus(jc?.status || "", latest?.jobStatus);
      setStatus(resolvedStatus);

      // Fetch the results payload as part of the same load — keeps this to a
      // single spinner instead of bouncing into a second one once status
      // resolves to "completed".
      if (resolvedStatus === "completed") {
        try {
          const outputRes = await axiosInstance.get(`/api/bid-multiplier/output/${id}`);
          const records: any[] = outputRes.data?.result?.["bid-multiplier"];
          if (Array.isArray(records) && records.length > 0) {
            const { columns, rows } = parseBidMultiplierOutput(records);
            setOutputColumns(columns);
            setOutputRows(rows);
            setOutputUnavailable(false);
          } else {
            setOutputUnavailable(true);
          }
        } catch {
          setOutputUnavailable(true);
        }
      }
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (status === "running" || status === "pending" || status === "unknown") {
      timerRef.current = setInterval(load, 15_000);
    } else if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [status, load]);

  if (!id) return null;

  if (loading) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "60vh", gap: 16, background: "var(--bg-base)", fontFamily: "var(--font-sans)" }}>
        <Spin size="large" />
        <span style={{ color: "var(--text-secondary)", fontSize: 13, fontFamily: "var(--font-sans)" }}>Loading…</span>
      </div>
    );
  }

  if (notFound) {
    return (
      <div style={{ padding: "80px 24px", textAlign: "center", fontFamily: "var(--font-sans)" }}>
        <div style={{ fontSize: 44, marginBottom: 16, opacity: 0.6 }}>🎯</div>
        <p style={{ fontSize: 15, fontWeight: 500, color: "var(--text-secondary)", marginBottom: 16 }}>Job not found</p>
        <button onClick={() => navigate("/bid-optimizer")} style={linkBtnStyle}>← Back to list</button>
      </div>
    );
  }

  if (status === "completed" && !outputUnavailable) {
    return (
      <BidOptimizerResults
        jobConfigId={id}
        name={name}
        jobConfigData={jobConfigData}
        columns={outputColumns}
        rows={outputRows}
      />
    );
  }

  if (status === "completed" && outputUnavailable) {
    return (
      <div style={{ padding: "28px 32px", maxWidth: 1100, margin: "0 auto", fontFamily: "var(--font-sans)" }}>
        <button onClick={() => navigate("/bid-optimizer")} style={linkBtnStyle}>← Back to list</button>
        <div style={{ marginTop: 24, padding: "48px 32px", textAlign: "center", borderRadius: "var(--radius)", background: "var(--bg-surface)", border: "1px solid var(--border-subtle)" }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text-primary)", marginBottom: 8 }}>Results not available yet</div>
          <p style={{ fontSize: 13, color: "var(--text-secondary)", maxWidth: 420, margin: "0 auto" }}>
            This job is marked complete but output hasn't finished writing. Refresh in a moment.
          </p>
        </div>
      </div>
    );
  }

  // ── Still processing / queued / failed ──
  const m = STATUS_META[status] || STATUS_META.unknown;
  return (
    <div style={{ padding: "28px 32px", maxWidth: 1100, margin: "0 auto", fontFamily: "var(--font-sans)" }}>
      <button onClick={() => navigate("/bid-optimizer")} style={linkBtnStyle}>← Back to list</button>

      <div style={{
        marginTop: 24, padding: "48px 32px", textAlign: "center", borderRadius: "var(--radius)",
        background: "var(--bg-surface)", border: "1px solid var(--border-subtle)",
      }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text-primary)", marginBottom: 8 }}>{name}</div>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 12px", borderRadius: 20, background: `${m.color}1a`, color: m.color, fontSize: 12, fontWeight: 500, marginBottom: 20 }}>
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: m.color, animation: status === "failed" ? "none" : "pulseDot 1.5s ease-in-out infinite" }} />
          {m.label}
        </div>

        {status === "failed" ? (
          <p style={{ fontSize: 13, color: "var(--text-secondary)", maxWidth: 420, margin: "0 auto" }}>
            This job failed to complete. Delete it from the list and try again, or check the input data for issues.
          </p>
        ) : (
          <p style={{ fontSize: 13, color: "var(--text-secondary)", maxWidth: 420, margin: "0 auto" }}>
            This job is still processing. Results will appear here automatically once it completes — this page refreshes every 15 seconds.
          </p>
        )}
      </div>

      <style>{`
        @keyframes pulseDot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.4; transform: scale(0.7); }
        }
      `}</style>
    </div>
  );
}

const linkBtnStyle: React.CSSProperties = {
  padding: "7px 14px", borderRadius: "var(--radius)", background: "none",
  color: "var(--text-tertiary)", border: "1px solid var(--border-default)",
  fontSize: 12, cursor: "pointer", fontFamily: "var(--font-sans)",
};
